/**
 * AccountSelect — the global account filter, living in the top bar's right
 * utility cluster (rebrand phase 2 — previously it sat in the slot where the
 * page title used to be). Custom button + .pop-in menu (mirrors the avatar
 * menu) rather than a native <select>, so header controls feel consistent.
 * Pill-shaped chip, matching the reference's rounded filter/stat-chip
 * anatomy. The choice is persisted (see selectedAccountStore).
 */
import { useState, useRef, useEffect } from 'react';
import { useAccounts } from '@/api/account';
import { useSelectedAccount } from '@/store/selectedAccountStore';

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function MenuItem({
  label, active, muted, onClick,
}: { label: string; active: boolean; muted?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '8px 10px',
        borderRadius: 'var(--radius-sm)',
        background: active ? 'var(--brand-soft)' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        fontWeight: active ? 600 : 400,
        color: active ? 'var(--brand)' : muted ? 'var(--text-muted)' : 'var(--text)',
        transition: 'background 0.1s, color 0.1s',
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-inset)';
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </span>
      <span style={{ flexShrink: 0, width: 14, color: 'var(--brand)', visibility: active ? 'visible' : 'hidden' }}>
        <CheckIcon />
      </span>
    </button>
  );
}

export function AccountSelect() {
  const { data: accounts = [], isLoading } = useAccounts();
  const accountId = useSelectedAccount((s) => s.accountId);
  const setAccountId = useSelectedAccount((s) => s.setAccountId);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // If a persisted id points at an account that no longer exists (deleted),
  // drop back to the portfolio-wide view rather than showing a stale label.
  useEffect(() => {
    if (accountId && accounts.length > 0 && !accounts.some((a) => a.id === accountId)) {
      setAccountId(null);
    }
  }, [accountId, accounts, setAccountId]);

  // Close on outside click / Escape (same pattern as the user menu).
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = accountId ? accounts.find((a) => a.id === accountId) : null;
  const label = selected ? selected.name : 'All accounts';

  function choose(id: string | null) {
    setAccountId(id);
    setOpen(false);
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Filter by account — currently ${label}`}
        className="focus-ring pressable"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          height: 32,
          boxSizing: 'border-box',
          padding: '0 12px',
          background: open ? 'var(--surface-inset)' : 'var(--search-pill-bg)',
          border: '1px solid var(--search-pill-border)',
          borderRadius: 'var(--radius-pill)',
          cursor: 'pointer',
          transition: 'background 0.12s',
        }}
        onMouseEnter={(e) => { if (!open) (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-inset)'; }}
        onMouseLeave={(e) => { if (!open) (e.currentTarget as HTMLButtonElement).style.background = 'var(--search-pill-bg)'; }}
      >
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
            fontWeight: 600,
            color: 'var(--text)',
            whiteSpace: 'nowrap',
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {label}
        </span>
        <svg
          width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
          style={{ color: 'var(--text-subtle)', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Select account"
          className="pop-in"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            minWidth: 230,
            maxHeight: 380,
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-panel)',
            boxShadow: 'var(--shadow-2)',
            zIndex: 30,
            padding: 4,
            ['--pop-origin' as unknown as string]: 'top left',
          }}
        >
          <MenuItem label="All accounts" active={accountId === null} muted onClick={() => choose(null)} />
          {accounts.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
          )}
          {accounts.map((a) => (
            <MenuItem key={a.id} label={a.name} active={a.id === accountId} onClick={() => choose(a.id)} />
          ))}
          {!isLoading && accounts.length === 0 && (
            <div style={{ padding: '8px 10px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--text-subtle)' }}>
              No accounts yet
            </div>
          )}
        </div>
      )}
    </div>
  );
}
